import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetDischargeCardById, UpdateDischargeCard } from "../../../../api/ipd/dischargeCardAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";

// Validation schema using Yup
const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD Entry is required"),
    icdx: yup.string().nullable(),
    finalDiagnosis: yup.string().required("Final Diagnosis is required"),
    complaint: yup.string().nullable(),
    historyOfPresentIllness: yup.string().required("History of present illness is required"),
    historyOfPastIllness: yup.string().required("History of past illness is required"),
    nextFollowup: yup.string().required("Next followup is required"),
    medicalAdviceDetails: yup.array().of(
        yup.object().shape({
            medicineId: yup.string().required("Medicine name is required"),
            categoryId: yup.string().required("Category is required"),
            dose: yup
                .number()
                .typeError("dose must be a number")
                .nullable(),
            doseRemark: yup.string().nullable(),
            frequency: yup.string().nullable(),
            time: yup.string().nullable(),
            days: yup.number().nullable(),
            remark: yup.string().nullable()
        })
    ),
});

const EditPatientHistoryEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [ipdEntries, setIpdEntries] = useState([]);
    const [medicines, setMedicines] = useState([]);
    const [categories, setCategories] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            medicalAdviceDetails: [
                { 
                    medicineName: "", 
                    categoryId: "", 
                    dose: "",
                    doseRemark: "",
                    frequency: "", 
                    time: "", 
                    days: "", 
                    remark: "" 
                }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "medicalAdviceDetails"
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchMedicines = async () => {
            try {
                const response = await GetItems();
                if (response.success) {
                    setMedicines(response.data);
                } else {
                    showToast("error", response.message);
                }
            } catch (error) {
                showToast("error", error.message);
            }
        };
    
        const fetchCategories = async () => {
            try {
                const response = await GetCategoriesMedicalStore();
                if (response.success) {
                    setCategories(response.data);
                } else {
                    showToast("error", response.message);
                }
            } catch (error) {
                showToast("error", error.message);
            }
        };

    const fetchData = async () => {
        try {
            const response = await GetDischargeCardById(id); // Make sure login function returns token
            if (response.success) {
                Object.entries(response.data).forEach(([key, value]) => {
                    // Handle main object dates
                    if (["date"].includes(key) && value) {
                        setValue(key, value.split("T")[0]);
                        return;
                    }

                    if (key === "medicalAdviceDetails" && Array.isArray(value)) {
                        const formattedCharges = value.map((item) => ({
                            ...item,
                            medicineId: item.medicineId?._id,
                            categoryId: item.categoryId?._id,
                            date: item.date ? item.date.split("T")[0] : "",
                        }));
                        setValue(key, formattedCharges);
                        return;
                    }
                    // Default
                    setValue(key, value);
                });

                setPatientDetails(response.data?.ipdEntryId || {});
                setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');

            } else {
                // setError(response.message);
                showToast("error", response.message);
            }
        } catch (error) {
            // setError(error); // Handle login errors
            showToast("error", error);
        }
    };

    // Fetch existing data
    useEffect(
        () => {
            if (id) {
                fetchIpdEntries().then(() => {
                fetchCategories().then(() => {
                    fetchMedicines().then(() => {
                    fetchData();
                });
                });
                });
            }
        },
        [id, setValue]
    );

    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });

            const response = await UpdateDischargeCard(id, data);
            if (response.success) {
                showToast("success", response.message);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="Discharge Card" title="Edit Discharge Card Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Edit Discharge Card Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/discharge-card-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List Discharge Card Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List Discharge Card Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <div className="row">
                                        <div className="col-6">
                                            <label>Room No</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.roomId?.roomNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-12 mt-3">
                                            <label>Icdx</label>
                                            <textarea className="form-control" {...register("icdx")} rows={5} />
                                            {errors.icdx && <span className="text-danger">{errors.icdx.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Final Diagnosis</label>
                                            <textarea className="form-control" {...register("finalDiagnosis")} rows={5} />
                                            {errors.finalDiagnosis && <span className="text-danger">{errors.finalDiagnosis.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Complaint</label>
                                            <textarea className="form-control" {...register("complaint")} rows={5} />
                                            {errors.complaint && <span className="text-danger">{errors.complaint.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>History Of Present Illness</label>
                                            <textarea className="form-control" {...register("historyOfPresentIllness")} rows={5} />
                                            {errors.historyOfPresentIllness && <span className="text-danger">{errors.historyOfPresentIllness.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>History Of Past Illness</label>
                                            <textarea className="form-control" {...register("historyOfPastIllness")} rows={5} />
                                            {errors.historyOfPastIllness && <span className="text-danger">{errors.historyOfPastIllness.message}</span>}
                                        </div>
                                        <div className="col-12 mt-3">
                                            <label>Next Followup</label>
                                            <textarea className="form-control" {...register("nextFollowup")} rows={5} />
                                            {errors.nextFollowup && <span className="text-danger">{errors.nextFollowup.message}</span>}
                                        </div>
                                    </div>

                                    <h5 className="text-primary mt-4">Medical Advice Details</h5>

                                    {fields.map((field, index) => (
                                        <div className="row border p-2 mb-2" key={field.id}>
                                            <div className="col-2">
                                                <label>Medicine Name</label>
                                                <select placeholder="Name" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.medicineId`)} >
                                                    <option value="">Select Medicine</option>
                                                    {medicines.map((medicine) => (
                                                        <option key={medicine._id} value={medicine._id}>{medicine.name}</option>
                                                    ))}
                                                </select>
                                                {errors.medicalAdviceDetails?.[index]?.medicineId && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.medicineId.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Category</label>
                                                <select placeholder="Category" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.categoryId`)} >
                                                    <option value="">Select Category</option>
                                                    {categories.map((category) => (
                                                        <option key={category._id} value={category._id}>{category.name}</option>
                                                    ))}
                                                </select>
                                                {errors.medicalAdviceDetails?.[index]?.categoryId && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.categoryId.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Dose</label>
                                                <input type="number" placeholder="Dose" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.dose`)} />
                                                {errors.medicalAdviceDetails?.[index]?.dose && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.dose.message}</span>}
                                            </div>
                                            <div className="col-2">
                                                <label>Dose Remark</label>
                                                <textarea type="number" placeholder="Dose Remark" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.doseRemark`)} />
                                                {errors.medicalAdviceDetails?.[index]?.doseRemark && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.doseRemark.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Frequency</label>
                                                <select
                                                    placeholder="Frequency"
                                                    className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.frequency`)}
                                                >
                                                    <option value="">Select Frequency</option>
                                                    <option value="OD ONCE DAILY">OD ONCE DAILY</option>
                                                    <option value="BD TWICE DAILY">BD TWICE DAILY</option>
                                                    <option value="TDS THREE TIMES DAILY">TDS THREE TIMES DAILY</option>
                                                    <option value="QID FOUR TIMES DAILY">QID FOUR TIMES DAILY</option>
                                                    <option value="HS BEFORE SLEEPING">HS BEFORE SLEEPING</option>
                                                    <option value="SOS AS NEEDED">SOS AS NEEDED</option>
                                                    <option value="PRN WHEN REQUIRED">PRN WHEN REQUIRED</option>
                                                    <option value="Q4H EVERY 4 HOURS">Q4H EVERY 4 HOURS</option>
                                                    <option value="Q6H EVERY 6 HOURS">Q6H EVERY 6 HOURS</option>
                                                    <option value="Q8H EVERY 8 HOURS">Q8H EVERY 8 HOURS</option>
                                                    <option value="Q12H EVERY 12 HOURS">Q12H EVERY 12 HOURS</option>
                                                    <option value="WEEKLY">WEEKLY</option>
                                                    <option value="MONTHLY">MONTHLY</option>
                                                </select>

                                                {errors.medicalAdviceDetails?.[index]?.frequency && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.frequency.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Time</label>
                                                <input type="time" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.time`)} />
                                                {errors.medicalAdviceDetails?.[index]?.time && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.time.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Days</label>
                                                <input type="number" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.days`)} />
                                                {errors.medicalAdviceDetails?.[index]?.days && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.days.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>remark</label>
                                                <textarea type="number" className="form-control"
                                                    {...register(`medicalAdviceDetails.${index}.remark`)} />
                                                {errors.medicalAdviceDetails?.[index]?.remark && <span className="text-danger">{errors.medicalAdviceDetails?.[index]?.remark.message}</span>}
                                            </div>

                                            <div className="col-12 mt-2 text-end">
                                                <button type="button" className="btn btn-danger btn-sm"
                                                    onClick={() => remove(index)}>
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-secondary mt-2"
                                        onClick={() => append({})}
                                    >
                                        + Add Child
                                    </button>
                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EditPatientHistoryEntry;
